import * as React from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import './conversationLook.css';
import '../SkillRunner.css';
import '../BankOfChangesRunner.css';

export type RunActivityCluster = { stage: string; lines: string[] };

export interface RunActivityProps {
  /** `stopped`: the lawyer stopped it, or the run was cut off. */
  state: 'running' | 'done' | 'failed' | 'stopped';
  clusters: RunActivityCluster[];
  /** The one line that sums the run up: what it is doing now, or what was done. */
  summary: string;
  toggleTitle: string;
  titleOf: (cluster: RunActivityCluster) => string;
  /** What a stage opens to; all of its lines when not given. `current` is the stage a running run is in. */
  linesOf?: (cluster: RunActivityCluster, current: boolean) => string[];
  rtl?: boolean;
}

/**
 * The progress of one run in the server, the way a finished tool run is shown
 * in a chat: one line that sums it up, which opens to its stages, and each
 * stage opens to what the server reported in it. Collapsed by default — while
 * the run goes on the line says what it is doing right now. Shared by the
 * workflow's conversation and by "Ask".
 */
export const RunActivity: React.FC<RunActivityProps> = ({ state, clusters, summary, toggleTitle, titleOf, linesOf, rtl = true }) => {
  const [open, setOpen] = React.useState(false);
  const [openStages, setOpenStages] = React.useState<Record<number, boolean>>({});
  const running = state === 'running';
  const hasStages = clusters.length > 0;
  const Closed = rtl ? ChevronLeft : ChevronRight;

  return (
    <div className={`boc-activity boc-activity--${state}`}>
      <button
        type="button"
        className="boc-activity-header"
        aria-expanded={open}
        title={toggleTitle}
        disabled={!hasStages}
        onClick={() => setOpen((value) => !value)}
      >
        {running && (
          <span className="skill-runner-busy boc-activity-spinner">
            <RefreshCw size={12} className="spinning" aria-hidden />
          </span>
        )}
        <span className="skill-runner-turn-content boc-activity-summary">{summary}</span>
        {hasStages && (open ? <ChevronDown size={14} aria-hidden /> : <Closed size={14} aria-hidden />)}
      </button>

      {open && hasStages && (
        <div className="boc-activity-box">
          {clusters.map((cluster, index) => {
            const current = running && index === clusters.length - 1;
            const lines = linesOf ? linesOf(cluster, current) : cluster.lines;
            const stageOpen = Boolean(openStages[index]);
            return (
              <div key={`${cluster.stage}-${index}`} className="boc-activity-stage">
                <button
                  type="button"
                  className="boc-activity-stage-title"
                  aria-expanded={stageOpen}
                  onClick={() => setOpenStages((value) => ({ ...value, [index]: !value[index] }))}
                >
                  <span>{titleOf(cluster)}</span>
                  {current && <RefreshCw size={11} className="spinning" aria-hidden />}
                  {stageOpen ? <ChevronDown size={13} aria-hidden /> : <Closed size={13} aria-hidden />}
                </button>
                {stageOpen && (
                  <div className="boc-activity-detail">
                    {lines.map((line, lineIndex) => (
                      <div key={lineIndex} dir="auto">
                        {line}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
